// Stub da biblioteca react-native-map-link para uso web

export interface MapLinkOptions {
  latitude?: number
  longitude?: number
  sourceLatitude?: number
  sourceLongitude?: number
  title?: string
  app?: string
  dialogTitle?: string
  dialogMessage?: string
  cancelText?: string
  appsWhiteList?: string[]
  appTitles?: { [key: string]: string }
  directionsMode?: 'driving' | 'walking' | 'transit' | 'bicycling'
  alwaysIncludeGoogle?: boolean
  googleForceLatLon?: boolean
}

export interface MapApp {
  id: string
  name: string
  icon: string
  open: () => void
}

// Constantes dos apps
export const APPS = {
  APPLE_MAPS: 'apple-maps',
  GOOGLE_MAPS: 'google-maps',
  CITYMAPPER: 'citymapper',
  UBER: 'uber',
  LYFT: 'lyft',
  WAZE: 'waze',
  YANDEX: 'yandex',
  MOOVIT: 'moovit',
};

// Função principal para mostrar localização
export const showLocation = (options: MapLinkOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    console.log('showLocation called with:', options);

    if (!options.latitude || !options.longitude) {
      reject(new Error('Latitude and longitude are required'));
      return;
    }

    try {
      const app = options.app || APPS.GOOGLE_MAPS;
      let url = '';

      switch (app) {
        case APPS.GOOGLE_MAPS:
          if (options.sourceLatitude && options.sourceLongitude) {
            url = `https://www.google.com/maps/dir/${options.sourceLatitude},${options.sourceLongitude}/${options.latitude},${options.longitude}`;
          } else {
            url = `https://www.google.com/maps?q=${options.latitude},${options.longitude}`;
            if (options.title) {
              url += `&query=${encodeURIComponent(options.title)}`;
            }
          }
          break;

        case APPS.APPLE_MAPS:
          if (options.sourceLatitude && options.sourceLongitude) {
            url = `http://maps.apple.com/?saddr=${options.sourceLatitude},${options.sourceLongitude}&daddr=${options.latitude},${options.longitude}`;
          } else {
            url = `http://maps.apple.com/?q=${options.latitude},${options.longitude}`;
          }
          break;

        case APPS.WAZE:
          url = `https://waze.com/ul?ll=${options.latitude},${options.longitude}&navigate=yes`;
          break;

        case APPS.CITYMAPPER:
          if (options.sourceLatitude && options.sourceLongitude) {
            url = `https://citymapper.com/directions?startcoord=${options.sourceLatitude},${options.sourceLongitude}&endcoord=${options.latitude},${options.longitude}`;
          } else {
            url = `https://citymapper.com/directions?endcoord=${options.latitude},${options.longitude}`;
          }
          break;

        case APPS.UBER:
          url = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[latitude]=${options.latitude}&dropoff[longitude]=${options.longitude}`;
          break;

        case APPS.LYFT:
          url = `https://lyft.com/ride?id=lyft&destination[latitude]=${options.latitude}&destination[longitude]=${options.longitude}`;
          break;

        default:
          url = `https://www.google.com/maps?q=${options.latitude},${options.longitude}`;
      }

      window.open(url, '_blank');
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

// Função para obter apps disponíveis
export const getApps = (options: MapLinkOptions = {}): Promise<MapApp[]> => {
  return new Promise((resolve) => {
    const { appsWhiteList, appTitles = {} } = options;

    const allApps: MapApp[] = [
      {
        id: APPS.GOOGLE_MAPS,
        name: appTitles[APPS.GOOGLE_MAPS] || 'Google Maps',
         icon: require('../../../node_modules/react-native-map-link/src/images/google-maps.png'),
        open: () => {
          showLocation({ ...options, app: APPS.GOOGLE_MAPS });
        },
      },
      {
        id: APPS.APPLE_MAPS,
        name: appTitles[APPS.APPLE_MAPS] || 'Apple Maps',
         icon: require('../../../node_modules/react-native-map-link/src/images/apple-maps.png'),
        open: () => {
          showLocation({ ...options, app: APPS.APPLE_MAPS });
        },
      },
      {
        id: APPS.WAZE,
        name: appTitles[APPS.WAZE] || 'Waze',
        icon: require('../../../node_modules/react-native-map-link/src/images/waze.png'),
        open: () => {
          showLocation({ ...options, app: APPS.WAZE });
        },
      },
    ];

    let availableApps = allApps;

    // Aplicar whitelist se fornecida
    if (appsWhiteList && appsWhiteList.length > 0) {
      availableApps = availableApps.filter((app) => appsWhiteList.includes(app.id));
    }

    // Simular que todos os apps estão "disponíveis" na web
    resolve(availableApps);
  });
};

// Função para mostrar seletor de apps
export const askAppChoice = (options: MapLinkOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    getApps(options)
      .then((apps) => {
        if (apps.length === 0) {
          reject(new Error('No map apps available'));
          return;
        }

        // Simular escolha do usuário - na web, vamos usar o primeiro app (Google Maps)
        const selectedApp = apps.find((app) => app.id === APPS.GOOGLE_MAPS) || apps[0];

        console.log(`User selected: ${selectedApp.name}`);
        selectedApp.open();
        resolve();
      })
      .catch(reject);
  });
};

// Exportação padrão
export default {
  showLocation,
  getApps,
  askAppChoice,
  APPS,
};
